import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { FaShoppingCart, FaSearch, FaHeart, FaStar } from "react-icons/fa";
import { useStore } from "../context/Storecontext";
import { getALLproducts } from "../services/productService";
import { getcategorylist } from "../services/categoryService";

const IMG_BASE =
  import.meta.env.VITE_IMG_URL ?? "http://localhost:8000/storage";

const mapApiProduct = (p) => {
  const price = 20 + (p.name.charCodeAt(0) % 50);
  const oldPrice = price + 10;
  const primary = p.images?.find((img) => img.is_primary) || p.images?.[0];
  const img1 = primary
    ? primary.image.startsWith("http")
      ? primary.image
      : `${IMG_BASE}/${primary.image}`
    : "/img/product/placeholder.jpg";
  const second = p.images?.length > 1 ? p.images[1] : primary;
  const img2 = second
    ? second.image.startsWith("http")
      ? second.image
      : `${IMG_BASE}/${second.image}`
    : img1;
  const badge =
    p.name.charCodeAt(1) % 3 === 0
      ? ["Sale"]
      : p.name.charCodeAt(1) % 3 === 1
        ? ["Sale", "New"]
        : [];
  return {
    id: p.product_id,
    product_id: p.product_id,
    name: p.name,
    category: p.category?.name || "",
    category_id: p.category_id,
    price,
    oldPrice,
    img1,
    img2,
    badge,
    description: p.description,
    additional_information: p.additional_information,
    status: p.status,
    images: p.images || [],
  };
};

const getCategoryIdsToFilter = (catId, categories) => {
  if (!catId || catId === "All") return [];
  const ids = [catId];
  const addChildren = (parentId) => {
    const children = categories.filter((c) => c.parent_id === parentId);
    for (const child of children) {
      ids.push(child.category_id);
      addChildren(child.category_id);
    }
  };
  addChildren(catId);
  return ids;
};

export default function ShopListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryParam = searchParams.get("category");

  const [allProducts, setAllProducts] = useState([]);
  const [apiCategories, setApiCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const { addToCart, addToWishlist, isInWishlist } = useStore();

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [prodRes, catRes] = await Promise.all([
          getALLproducts(),
          getcategorylist(),
        ]);
        const prodData = prodRes.data?.data || prodRes.data || [];
        const catData = catRes.data?.data || catRes.data || [];
        setAllProducts(prodData.map(mapApiProduct));
        setApiCategories(catData);
      } catch (err) {
        console.error("Failed to load shop list data:", err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  let filtered = [...allProducts];
  if (categoryParam && categoryParam !== "All") {
    const allowedIds = getCategoryIdsToFilter(categoryParam, apiCategories);
    filtered = allProducts.filter((p) => allowedIds.includes(p.category_id));
  }

  const handleAddToCart = (p) => {
    addToCart(p);
    navigate("/cart");
  };

  const handleAddToWishlist = (p) => {
    addToWishlist(p);
    navigate("/wishlist");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32 text-sm text-gray-400">
        Loading products...
      </div>
    );
  }

  return (
    <div>
      <div
        className="w-full h-32 bg-cover bg-center relative flex items-center p-8 border-b border-gray-100 overflow-hidden"
        style={{ backgroundImage: "url(/img/bg/banner18.jpg)" }}
      >
        <nav className="text-xs text-gray-400 font-medium tracking-wide justify-center items-center m-auto text-center">
          <h2 className="text-2xl text-black font-semibold mb-2">Shop</h2>
          <Link to="/" className="hover:text-[#43a047] transition-colors">
            Home
          </Link>
          <span className="mx-2 text-gray-300">/</span>
          <span className="text-gray-600">Shop List</span>
        </nav>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-10">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <aside className="lg:w-64 flex-shrink-0">
            <div className="mb-8">
              <h3 className="font-bold text-dark mb-4 pb-2 border-b border-border-color uppercase text-sm tracking-wider">
                Categories
              </h3>
              <ul className="space-y-2">
                <li>
                  <button
                    onClick={() => setSearchParams({})}
                    className={`text-sm w-full text-left py-1 hover:text-primary transition-colors ${!categoryParam ? "text-primary font-semibold" : "text-gray-500"}`}
                  >
                    All{" "}
                    <span className="text-gray-400">
                      ({allProducts.length})
                    </span>
                  </button>
                </li>
                {apiCategories.map((c) => {
                  const allowedIds = getCategoryIdsToFilter(
                    c.category_id,
                    apiCategories,
                  );
                  const count = allProducts.filter((p) =>
                    allowedIds.includes(p.category_id),
                  ).length;
                  return (
                    <li key={c.category_id}>
                      <button
                        onClick={() =>
                          setSearchParams({ category: c.category_id })
                        }
                        className={`text-sm w-full text-left py-1 hover:text-primary transition-colors capitalize ${categoryParam === c.category_id ? "text-primary font-semibold" : "text-gray-500"}`}
                      >
                        {c.name}{" "}
                        <span className="text-gray-400">({count})</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </aside>

          {/* List */}
          <div className="flex-1 space-y-5">
            {filtered.map((p) => (
              <div
                key={p.id}
                className="flex gap-3 sm:gap-5 border border-border-color p-4 hover:shadow-md transition-shadow group bg-white"
              >
                <div className="w-24 h-24 sm:w-36 sm:h-36 flex-shrink-0 overflow-hidden bg-gray-50 relative">
                  <Link to={`/product-details/${p.product_id}`}>
                    <img
                      src={p.img1}
                      alt={p.name}
                      className="w-full h-full object-cover group-hover:opacity-0 transition-opacity absolute inset-0"
                    />
                    <img
                      src={p.img2}
                      alt={p.name}
                      className="w-full h-full object-cover"
                    />
                  </Link>
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-bold text-dark hover:text-primary mb-1">
                    <Link to={`/product-details/${p.product_id}`}>
                      {p.name}
                    </Link>
                  </h4>
                  <div className="flex items-center gap-1 mb-2">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <FaStar key={s} className="text-yellow-400" size={11} />
                    ))}
                    <span className="text-xs text-gray-400 ml-1">(5)</span>
                  </div>
                  <p className="text-xs text-gray-400 mb-2">{p.category}</p>
                  <p
                    className="text-sm text-gray-500 leading-relaxed mb-3 line-clamp-2"
                    dangerouslySetInnerHTML={{
                      __html:
                        p.description ||
                        "Lorem ipsum dolor sit amet, consectetur adipiscing elit.",
                    }}
                  />
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
                    <div className="flex gap-2 items-center">
                      <span className="text-primary font-bold">
                        ${p.price}.00
                      </span>
                      <span className="text-gray-400 line-through text-xs">
                        ${p.oldPrice}.00
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleAddToCart(p)}
                        title="Add to Cart"
                        className="w-8 h-8 border border-border-color flex items-center justify-center hover:bg-primary hover:text-white hover:border-primary transition-colors"
                      >
                        <FaShoppingCart size={13} />
                      </button>
                      <button
                        onClick={() => handleAddToWishlist(p)}
                        title="Add to Wishlist"
                        className={`w-8 h-8 border border-border-color flex items-center justify-center hover:bg-primary hover:text-white hover:border-primary transition-colors ${isInWishlist(p.id) ? "text-primary border-primary" : ""}`}
                      >
                        <FaHeart size={13} />
                      </button>
                      <Link
                        to={`/product-details/${p.product_id}`}
                        title="Quick View"
                        className="w-8 h-8 border border-border-color flex items-center justify-center hover:bg-primary hover:text-white hover:border-primary transition-colors"
                      >
                        <FaSearch size={13} />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
